import React, { useEffect, useRef, useState } from "react";
import {
  Collapse,
  Form,
  Button,
  message,
  Typography,
  Row,
  Col,
  Select,
} from "antd";
import ConsultantForm from "../CommonComponents/ConsultantForm";
import PatientProfile from "../CommonComponents/PatientProfile";
import ClinicalImpression from "../CommonComponents/ClinicalImpression";
import HistoryIllness from "../CommonComponents/HistoryIllness";
import Medication from "../CommonComponents/Medication";
import Nebulization from "../CommonComponents/Nebulization";
import Vitals from "../CommonComponents/Vitals";
import DischargeFunctionalStatus from "../DischargeFunctionalStatus";
import Comments from "../CommonComponents/Comments";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import dayjs from "dayjs";
import {
  getListReviewFormApi,
  getReviewFormApi,
  getPatientDetailsApi,
  printFormApi,
  shareIpFormApi,
  submitReviewFormApi,
  updateReviewFormApi,
  updateReviewFormHtmlApi,
} from "../../../../services/forms.services";
import {
  knownAllergiesOptions,
  pastMedicalOptions,
} from "../../../IpEMR/Options/historyOptions";
import {
  getLabelTagsWithFormDescription,
  getPastMedicalHistoryFormSummary,
  printFormSummary,
} from "../formsKeys";
import ReviewClinicalProgress from "./ReviewClinicalProgress";
import ReviewFormScales from "./ReviewFormScales";
import ReviewModal from "./ReviewModal";
import useQuery from "../../../../hooks/useQuery";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import assessmentIcon from "../../../../assets/images/nurseIcons/assessment.svg";
import { getPhysioInspectionAPI } from "../../../../services/physio.services";
import {
  isHtmlDocument,
  isWithinEditWindow,
} from "../CommonComponents/formRecords";
import PhysioFunctionalDiagnosisSummary from "../../../IpEMR/InspectionSummaries/FunctionalExaminationSummary/PhysioFunctionalDiagnosisSummary";
import FormContentLoader from "../FormContentLoader";
import FormDocumentView from "../CommonComponents/FormDocumentView";
import ShareModal from "../../Forms/DischargeSummary/ShareModal";
import "../ReviewForms.css";
import "../FormsIp.css";

export const ADMISSION_MODE_PRIMARY = "primary_hospital";
export const ADMISSION_MODE_HOME = "home";

const ADMISSION_MODE_OPTIONS = [
  { label: "Primary Hospital", value: ADMISSION_MODE_PRIMARY },
  { label: "Home", value: ADMISSION_MODE_HOME },
];

const ReviewForms = ({ patientData, role }) => {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const commonStyle = {
    // background: "#fff",
    boxShadow:
      "0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1)",
  };

  const [pressureSorePoints, setPressureSorePoints] = useState([]);
  const [referralFormData, setReferralFormData] = useState(null);
  const [editable, setEditable] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [details, setDetails] = useState(null);
  const [reviewList, setReviewList] = useState([]);
  const [open, setOpen] = useState(false);
  const [displayForm, setDisplayForm] = useState(null);
  const [isNewForm, setIsNewForm] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);
  const [admissionMode, setAdmissionMode] = useState(ADMISSION_MODE_PRIMARY);

  const [physioScales, setPhysioScales] = useState(null);
  const [physioAssessment, setPhysioAssessment] = useState(null);
  const [openShare, setOpenShare] = useState(false);
  const [sharePending, setSharePending] = useState(null);
  const isActive = useQuery()?.status === "current";
  const isReceptionist = role === "receptionist";
  const isPhysio = role === "physiotherapist";
  const therapist = isPhysio ? "physiotherapist" : "doctor";
  const hasInitialAssessment = isPhysio
    ? patientData?.physio_initial_assessment
    : patientData?.doctor_intial_assessment;
  const canEditForm = isActive && !isReceptionist && editable;
  const formTitle = isPhysio ? "Physio Review Form" : "Review Form";
  const functionalStatusHeader = isPhysio
    ? "Functional Status at Referral"
    : "Functional Status at Discharge / Referral";

  // The physio review documents the physiotherapist's own findings, so its
  // clinical content is sourced from the physio initial assessment - never from
  // the doctor's. Only the patient demographics stay shared between the two.
  const clinicalSource = isPhysio
    ? { present_illness: physioAssessment?.history, diagnosis: null }
    : {
        present_illness: details?.data?.present_illness,
        diagnosis: details?.data?.diagnosis,
      };

  const refreshPage = () => setRefresh((prev) => !prev);

  const applyReviewFormData = (record) => {
    if (!record?.data) return;
    setAdmissionMode(
      // mode_of_arrival is the current key; history.mode_of_admission is read
      // as a fallback so records saved before the rename still restore.
      record.data?.mode_of_arrival ||
        record.data?.history?.mode_of_admission ||
        ADMISSION_MODE_PRIMARY
    );
    form.setFieldsValue({
      ...record.data,
      clinical_progress: {
        ...record.data?.clinical_progress,
        date: record.data?.clinical_progress?.date
          ? dayjs(record.data.clinical_progress.date)
          : null,
      },
    });
    if (record.data?.pressure_sore) {
      setPressureSorePoints(record.data.pressure_sore);
    } else {
      setPressureSorePoints([]);
    }
  };


  const loadReviewFormDisplay = async (formId, savedHtml) => {
    if (!formId) {
      setDisplayForm(null);
      return;
    }
    if (isHtmlDocument(savedHtml)) {
      setDisplayForm(savedHtml);
      return;
    }
    try {
      const savedRes = await getReviewFormApi(formId);
      const saved = savedRes?.data?.data?.pdf_html ?? savedRes?.data?.pdf_html;
      if (isHtmlDocument(saved)) {
        setDisplayForm(saved);
        return;
      }
    } catch (err) {
      console.log(err);
    }
    return printFormApi(`form_type=review&form_id=${formId}`)
      .then((res) => {
        setDisplayForm(res?.data?.data ?? null);
      })
      .catch((err) => {
        console.log(err);
        setDisplayForm(null);
        message.error(
          err?.response?.data?.message || "Failed to load review form"
        );
      });
  };

  const fetchReviewList = (treatAsNewForm = isNewForm) => {
    const openId = referralFormData?.id;
    setIsContentReady(false);
    return getListReviewFormApi(Number(patientData?.admission?.id))
      .then((res) => {
        const list = (res?.data || []).filter(
          (row) => (row?.data?.therapist === "physiotherapist") === isPhysio
        );
        setReviewList(list);

        if (treatAsNewForm) {
          setReferralFormData(null);
          setDisplayForm(null);
          setEditable(isActive && !isReceptionist);
        } else {
          const selectedRecord =
            list.find((row) => row?.id === openId) ??
            list[list.length - 1] ??
            null;
          setReferralFormData(selectedRecord);
          applyReviewFormData(selectedRecord);
          if (isReceptionist || !isActive) {
            setEditable(false);
          } else {
            setEditable(selectedRecord?.id ? false : true);
          }
          return loadReviewFormDisplay(
            selectedRecord?.id,
            selectedRecord?.pdf_html
          );
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsContentReady(true);
      });
  };

  useEffect(() => {
    if (patientData?.admission?.id && hasInitialAssessment) {
      fetchReviewList();
      // Demographics are role-neutral and drive the printed document header.
      getPatientDetailsApi(patientData?.admission?.id)
        .then((res) => {
          setDetails(res?.data);
        })
        .catch((err) => console.log(err));
      if (isPhysio) {
        getPhysioInspectionAPI(patientData?.physio_initial_assessment)
          .then((res) => {
            setPhysioAssessment(res?.data?.data ?? res?.data ?? null);
          })
          .catch((err) => console.log(err));
      }
    }
  }, [
    patientData?.admission?.id,
    hasInitialAssessment,
    refresh,
    isActive,
    isReceptionist,
    isPhysio,
  ]);

  useEffect(() => {
    if (referralFormData?.data) {
      applyReviewFormData(referralFormData);
    } else if (isNewForm) {
      form.resetFields();
      setPressureSorePoints([]);
    }
  }, [referralFormData]);

  const handleModalAction = (record, isEdit) => {
    form.resetFields();
    setDisplayForm(null);
    setReferralFormData(record);
    setOpen(false);
    setEditable(isEdit && !isReceptionist);
    setIsNewForm(false);
    setIsContentReady(false);

    applyReviewFormData(record);

    loadReviewFormDisplay(record?.id, record?.pdf_html).finally(() => {
      setIsContentReady(true);
    });
  };

  const handleNewForm = () => {
    setIsNewForm(true);
    setReferralFormData(null);
    setAdmissionMode(ADMISSION_MODE_PRIMARY);
    form.resetFields();
    setPressureSorePoints([]);
    setDisplayForm(null);
    setEditable(true);
    setIsContentReady(true);
  };

  const onFinish = (values) => {
    const formData = {
      patient: details?.data?.patient,
      diagnosis: clinicalSource.diagnosis,
      ...(isPhysio
        ? {}
        : {
            medications: details?.data?.medications,
            vitals: details?.data?.vitals,
            nebulization: details?.data?.nebulization,
          }),
      past_medical_history: clinicalSource.present_illness
        ? getPastMedicalHistoryFormSummary(
            pastMedicalOptions,
            clinicalSource.present_illness
          )
        : "",
      known_allergies: clinicalSource.present_illness
        ? getLabelTagsWithFormDescription(
            knownAllergiesOptions,
            clinicalSource.present_illness
          )
        : "",
      past_surgical_history: clinicalSource.present_illness?.surgical_history
        ? clinicalSource.present_illness?.surgical_history
        : null,
      past_surgical_history_details: clinicalSource.present_illness
        ?.surgical_history_details
        ? clinicalSource.present_illness?.surgical_history_details
        : null,
      ...values,

      clinical_progress: {
        ...values?.clinical_progress,
        date: values?.clinical_progress?.date
          ? dayjs(values.clinical_progress.date).format("YYYY-MM-DD")
          : null, // Handle cases where the date is not provided
      },
      pressure_sore: isPhysio ? [] : pressureSorePoints,
      therapist,
      ...(isPhysio
        ? {
            // Top-level alongside `therapist`, not nested under history.
            mode_of_arrival: admissionMode,
            scales: physioScales ?? null,
            functional_diagnosis: physioAssessment?.functional_diagnosis ?? null,
          }
        : {}),
    };
    if (!referralFormData?.id && !isNewForm && reviewList?.length) {
      message.error(
        "Could not tell which form is open. Please reopen it from History and try again."
      );
      return;
    }
    if (referralFormData?.id) {
      updateReviewFormApi(referralFormData?.id, { data: formData })
        .then((res) => {
          message.success(res?.data?.message);
          setEditable(false);
          refreshPage();
        })
        .catch((err) => message.error(err?.response?.data?.message));
    } else {
      submitReviewFormApi({
        ip_admission: patientData?.admission?.id,
        data: formData,
      })
        .then((res) => {
          message.success(res?.data?.message);
          setIsNewForm(false);
          setEditable(false);
          fetchReviewList(false);
        })
        .catch((err) => message.error(err?.response?.data?.message));
    }

    // console.log(formData, "fo");
  };
  // TinyMCE edits are stored on the form's pdf_html field rather than the
  // structured `data`, so the printable document keeps the manual wording.
  const saveDocumentHtml = async (html) => {
    if (!referralFormData?.id) return false;
    const savedId = referralFormData.id;
    try {
      const res = await updateReviewFormHtmlApi(savedId, html);
      message.success(res?.data?.message || `${formTitle} saved.`);
      refreshPage();
      return true;
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to save. Please try again."
      );
      return false;
    }
  };

  // console.log(reviewList, referralFormData);
  return (
    <>
      {!hasInitialAssessment ? (
        <Row
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
          }}
        >
          <Col span={24} style={{ textAlign: "center" }}>
            <NoDataPage
              title={
                isPhysio
                  ? "Physiotherapist Initial Assessment Not Yet Submitted "
                  : "Initial Assessment Not Yet Submitted "
              }
              subtitle={"Kindly, submit initial assessment first"}
              image={assessmentIcon}
            />
          </Col>
        </Row>
      ) : (
        <div style={{ position: "relative" }}>
          {!displayForm && (
          <Typography.Title level={5} className="review-form-title">
            <div className="review-form-title-top">
              <span className="review-form-title-text">{formTitle}</span>
            </div>
            <div className="review-form-title-actions">
              {reviewList?.length > 0 && (
                <Button
                  className="ds-btn ds-btn--ghost"
                  onClick={() => setOpen(true)}
                >
                  History
                </Button>
              )}
              {isPhysio && editable && (
                <div className="review-form-admission-mode-field">
                  <label
                    className="review-form-admission-mode-label"
                    htmlFor="review-form-admission-mode"
                  >
                    Mode of Arrival
                  </label>
                <Select
                  id="review-form-admission-mode"
                  className="review-form-admission-mode"
                  value={admissionMode}
                  onChange={(value) => {
                    setAdmissionMode(value);
                  }}
                  options={ADMISSION_MODE_OPTIONS}
                  aria-label="Mode of admission"
                />
                </div>
              )}
            </div>
          </Typography.Title>
          )}
          {open && (
            <ReviewModal
              open={open}
              setOpen={setOpen}
              reviewList={reviewList}
              onShowClick={(record) => handleModalAction(record, false)}
              form={form}
              isActive={isActive}
            />
          )}
          <FormContentLoader
            loading={!isContentReady}
            tip="Loading review form..."
          >
          {canEditForm ? (
            <Form
              form={form}
              ref={formRef}
              layout="vertical"
              onFinish={onFinish}
              className="forms-ip-shell"
              initialValues={{
                ...referralFormData?.data,
                clinical_progress: {
                  ...referralFormData?.data?.clinical_progress,
                  date: referralFormData?.data?.clinical_progress?.date
                    ? dayjs(referralFormData?.data?.clinical_progress?.date) // Keep as dayjs object
                    : null,
                },
              }}
            >
              <Collapse
                expandIconPosition="right"
                className="custom-collapse"
                defaultActiveKey={[
                  "1",
                  "2",
                  "3",
                  "4",
                  "5",
                  "6",
                  "7",
                  "8",
                  "9",
                  "10",
                  "11",
                ]}
              >
                <Collapse.Panel
                  header="Patient Profile"
                  style={commonStyle}
                  key="1"
                >
                  <PatientProfile data={patientData?.admission} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Consultants"
                  style={commonStyle}
                  key="2"
                >
                  <ConsultantForm form={form} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Clinical Impression/ Diagnosis"
                  style={commonStyle}
                  key="3"
                >
                  {isPhysio ? (
                    <PhysioFunctionalDiagnosisSummary
                      formData={physioAssessment}
                    />
                  ) : (
                    <ClinicalImpression
                      form={form}
                      details={clinicalSource.diagnosis}
                    />
                  )}
                </Collapse.Panel>
                <Collapse.Panel
                  header="History of Present Illness"
                  style={commonStyle}
                  key="4"
                >
                  <HistoryIllness
                    form={form}
                    patientData={patientData?.admission}
                    data={clinicalSource.present_illness}
                    admissionMode={isPhysio ? admissionMode : undefined}
                  />
                </Collapse.Panel>
                {isPhysio && (
                  <Collapse.Panel
                    header="Scales"
                    style={commonStyle}
                    key="5"
                  >
                    <ReviewFormScales
                      onLoaded={setPhysioScales}
                      admissionId={patientData?.admission?.id}
                    />
                  </Collapse.Panel>
                )}
                {/* <Collapse.Panel
              header="Functional Status at the time of Admission"
              style={commonStyle}
              key="5"
            >
              <FunctionalStatus form={form} details={details} />
            </Collapse.Panel> */}
                <Collapse.Panel
                  header="Clinical Progress"
                  style={commonStyle}
                  key="6"
                >
                  <ReviewClinicalProgress
                    form={form}
                    setPressureSorePoints={setPressureSorePoints}
                    pressureSorePoints={pressureSorePoints}
                    hidePressureSore={isPhysio}
                  />
                </Collapse.Panel>
                {!isPhysio && (
                  <>
                    <Collapse.Panel
                      header="Medication"
                      style={commonStyle}
                      key="7"
                    >
                      <Medication form={form} details={details} />
                    </Collapse.Panel>
                    <Collapse.Panel
                      header="Nebulization"
                      style={commonStyle}
                      key="8"
                    >
                      <Nebulization form={form} details={details} />
                    </Collapse.Panel>
                  </>
                )}
                <Collapse.Panel
                  header={functionalStatusHeader}
                  style={commonStyle}
                  key="9"
                >
                  <DischargeFunctionalStatus form={form} />
                </Collapse.Panel>
                {!isPhysio && (
                  <Collapse.Panel header="Vitals" style={commonStyle} key="10">
                    <Vitals form={form} details={details} />
                  </Collapse.Panel>
                )}
                <Collapse.Panel
                  header="Comments"
                  style={{ ...commonStyle, marginBottom: "40px" }}
                  key="11"
                >
                  <Comments form={form} />
                </Collapse.Panel>
              </Collapse>
              <div
                className="ip-fixed-form-footer"
                style={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,

                  width: "100%",
                  background: "#fff",
                  paddingTop: "10px",
                  paddingRight: "16px",
                  boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.1)",
                  textAlign: "right",
                  zIndex: 1000, // Ensure it stays above other elements
                }}
              >
                {isActive && !["receptionist"]?.includes(role) && (
                  <Form.Item>
                    <Button
                      htmlType="submit"
                      type="primary"
                      className={btnStyles.lgBtn}
                    >
                      {referralFormData?.id ? "Update" : "Submit"}
                    </Button>
                  </Form.Item>
                )}
              </div>
            </Form>
          ) : !isActive && !referralFormData?.id ? (
            <NoDataPage
              title={`${formTitle} Not Available`}
              subtitle={`${formTitle} is not available for discharged patients`}
              image={assessmentIcon}
            />
          ) : isReceptionist && !referralFormData?.id ? (
            <NoDataPage
              title={`${formTitle} Not Available`}
              subtitle={`${formTitle} has not been submitted yet`}
              image={assessmentIcon}
            />
          ) : displayForm ? (
            <FormDocumentView
              html={displayForm}
              canEdit={
                isActive &&
                !isReceptionist &&
                !!referralFormData?.id &&
                isWithinEditWindow(referralFormData?.created_at)
              }
              onSave={saveDocumentHtml}
              leading={
                <div className="review-form-doc-leading">
                  <span className="review-form-title-text">{formTitle}</span>
                  {!editable && isActive && !isReceptionist && (
                    <Button
                      className="ds-btn ds-btn--ghost"
                      onClick={() => handleNewForm()}
                    >
                      New Form
                    </Button>
                  )}
                </div>
              }
              extraActions={
                <>
                  {reviewList?.length > 0 && (
                    <Button
                      className="ds-btn ds-btn--ghost"
                      onClick={() => setOpen(true)}
                    >
                      History
                    </Button>
                  )}
                  {referralFormData?.id ? (
                    <Button
                      className="ds-btn ds-btn--ghost"
                      onClick={() =>
                        printFormSummary(
                          "review",
                          referralFormData?.id,
                          displayForm
                        )
                      }
                    >
                      Print
                    </Button>
                  ) : null}
                </>
              }
              onShare={
                referralFormData?.id
                  ? (pendingHtml, onSaved) => {
                      setSharePending(
                        pendingHtml ? { pendingHtml, onSaved } : null
                      );
                      setOpenShare(true);
                    }
                  : undefined
              }
            />
          ) : referralFormData?.id ? (
            <FormContentLoader loading tip="Loading review summary..." />
          ) : null}
          {openShare && (
            <ShareModal
              openModal={openShare}
              setOpenModal={(open) => {
                setOpenShare(open);
                if (!open) setSharePending(null);
              }}
              id={referralFormData?.id}
              onBeforeSend={
                sharePending
                  ? async () => {
                      const ok = await saveDocumentHtml(sharePending.pendingHtml);
                      if (ok) sharePending.onSaved?.();
                      return ok;
                    }
                  : undefined
              }
              patientData={patientData}
              title={`Share ${formTitle}`}
              documentLabel={formTitle}
              shareApi={(payload) =>
                shareIpFormApi("review", referralFormData?.id, payload)
              }
            />
          )}
          </FormContentLoader>
        </div>
      )}
    </>
  );
};

export default ReviewForms;
